import { client } from '@/lib/api/client'
import {
  venueReservationBoardPath,
  venueReservationBookerContactPath,
  venueReservationCalendarPath,
  venueReservationPath,
  venueReservationSchedulePath,
  venueReservationsPath,
} from '@/lib/api/config'
import { toApiError } from '@/lib/api/errors'

import type {
  ReservationBoardView,
  ReservationBookerContactView,
  ReservationCalendarView,
  ReservationDetailView,
  ReservationListView,
  ReservationManualCreateInput,
  ReservationRescheduleInput,
} from './reservationTypes'
import { reservationListQueryParams, type ReservationListFilter } from './reservationViewModel'

/**
 * `GET .../reservation-calendar` (#666 §1) — 월간·주간 캘린더 요약.
 * 🚨 게이팅 대상이 아니다 — 이용권이 모두 만료돼도 항상 200.
 */
export async function fetchReservationCalendar(
  venueId: string,
  params: { from: string; to: string; courtId?: number }
): Promise<ReservationCalendarView> {
  try {
    const { data } = await client.get<ReservationCalendarView>(venueReservationCalendarPath(venueId), {
      params,
    })
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `GET .../reservation-board` (#666 §2) — 특정 날짜 코트별 시간대 현황.
 * 🚨 게이팅 대상이 아니다 — 이용권이 모두 만료돼도 항상 200.
 */
export async function fetchReservationBoard(
  venueId: string,
  params: { date: string; courtId?: number }
): Promise<ReservationBoardView> {
  try {
    const { data } = await client.get<ReservationBoardView>(venueReservationBoardPath(venueId), {
      params,
    })
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `GET .../reservations` (#666 §3) — 커서 페이지네이션.
 * 🚨 `cursor` 는 서버가 준 `nextCursor` 문자열을 그대로 되싣는다(해석·가공하지 않는다).
 */
export async function fetchReservations(
  venueId: string,
  filter: ReservationListFilter,
  cursor?: string
): Promise<ReservationListView> {
  try {
    const { data } = await client.get<ReservationListView>(venueReservationsPath(venueId), {
      params: reservationListQueryParams(filter, cursor),
    })
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `GET .../reservations/{reservationId}` (#666 §4) — 5블록 상세.
 * 🚨 다른 구장의 예약 id 는 404(403 아님 — id 열거 방지).
 */
export async function fetchReservationDetail(
  venueId: string,
  reservationId: number
): Promise<ReservationDetailView> {
  try {
    const { data } = await client.get<ReservationDetailView>(venueReservationPath(venueId, reservationId))
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `GET .../reservations/{reservationId}/booker-contact` (#666 §5) — 원본 연락처 전용 EP.
 * 🚨 호출 자체가 열람 기록으로 남는다 — "예약자 연락" 버튼을 누른 순간에만 호출한다(화면 진입 시
 * 미리 호출하지 않는다). `available=false` 여도 404 가 아니라 200 + 사유다.
 */
export async function fetchReservationBookerContact(
  venueId: string,
  reservationId: number
): Promise<ReservationBookerContactView> {
  try {
    const { data } = await client.get<ReservationBookerContactView>(
      venueReservationBookerContactPath(venueId, reservationId)
    )
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `PATCH .../reservations/{reservationId}/schedule` (#669 §1) — 코트·날짜·시간 이동.
 * 🚨 응답은 `GET .../reservations/{id}` 와 완전히 같은 타입(변경 적용 후 값)이다. 호출부는 이 응답을
 * 상세 쿼리 캐시에 직접 반영하고, **성공 후 상세를 다시 조회하지 않는다**(같은 트랜잭션의 결과가
 * 최신값 — #669 §1-2).
 */
export async function rescheduleReservation(
  venueId: string,
  reservationId: number,
  input: ReservationRescheduleInput
): Promise<ReservationDetailView> {
  try {
    const { data } = await client.patch<ReservationDetailView>(
      venueReservationSchedulePath(venueId, reservationId),
      input
    )
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `POST .../reservations` (#675 §2-2) — 전화·현장(수기) 예약 등록.
 * 🚨 목록 `GET`과 같은 경로다(REST 정석, 신규 path 함수 불필요). 응답은 `201` + 상세 5블록과 완전히
 * 같은 타입 — 등록 직후 상세를 다시 조회하지 않는다(`rescheduleReservation`과 동일 계약).
 */
export async function createManualReservation(
  venueId: string,
  input: ReservationManualCreateInput
): Promise<ReservationDetailView> {
  try {
    const { data } = await client.post<ReservationDetailView>(venueReservationsPath(venueId), input)
    return data
  } catch (error) {
    throw toApiError(error)
  }
}
